import {useBreakpointValue, useColorMode, useToken} from '@chakra-ui/react';
import {mode} from '@chakra-ui/theme-tools';
import {format} from 'date-fns';
import type {ComponentProps} from 'react';
import React, {useId} from 'react';
import type {BarProps} from 'recharts';
import {Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis} from 'recharts';

import ChartTooltip from './ChartTooltip';
import ChartXAxisTick from './ChartXAxis';

type Props = ComponentProps<typeof BarChart>;

const StackedBarChart = ({children, ...props}: Props) => {
  const id = useId();
  const responsiveInterval = useBreakpointValue({base: 1, md: 3, lg: 2, xl: 0});
  const strokeColorLight = useToken(`colors`, `gray.200`);
  const strokeColorDark = useToken(`colors`, `whiteAlpha.500`);
  const {colorMode} = useColorMode();
  const dataWithGap = props.data?.map(item => {
    // TODO: better typing
    let updatedItem = {} as any;

    Object.keys(item).forEach((key, index) => {
      //TODO calculate gap based on the number of keys
      const gapValue = 50;
      // If index is 0, it's the first key so add gap before
      if (index === 0) {
        updatedItem = {[`${key}_gap`]: gapValue, ...updatedItem};
      }

      updatedItem[key] = item[key];
      updatedItem[`${key}_gap`] = gapValue; // adds a new item for each key
    });

    return updatedItem;
  });
  const stackedChildren = React.Children.toArray(children).flatMap(child => {
    if (!React.isValidElement(child)) return [];

    const gap = (
      <Bar
        key={`${(child as any).props.dataKey}_gap`}
        dataKey={`${(child as any).props.dataKey}_gap`}
        stackId={id}
        fill={`transparent`}
      />
    );

    // If index is 0, it's the first child so add gap before
    return [React.cloneElement(child, {key: (child as any).props.dataKey, stackId: id} as BarProps), gap];
  });
  return (
    <ResponsiveContainer width={`100%`} height={`100%`}>
      <BarChart
        barCategoryGap={1} // Adjust this value
        barSize={10} // Adjust this value
        barGap={50}
        {...props}
        data={dataWithGap}
      >
        <CartesianGrid
          stroke={mode(strokeColorLight, strokeColorDark)({colorMode})}
          vertical={true}
          horizontal={false}
        />
        <XAxis
          axisLine={false}
          padding={{left: 10, right: 10}}
          dataKey={`date`}
          interval={responsiveInterval}
          tick={ChartXAxisTick}
          tickFormatter={tickItem => format(new Date(tickItem).getTime(), `d`)}
        />
        <Tooltip content={<ChartTooltip />} />
        {stackedChildren}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default StackedBarChart;
export type {Props as WidgetChartProps};
